import type { NudgeSource } from '@cobliteam/hooks/src/useNudge/types';
import useNudges from '@cobliteam/hooks/src/useNudge/useNudges';
import { useEffect, useRef, useState } from 'react';
import NudgeCard from './NudgeCard';
import NudgeCollapsedRow from './NudgeCollapsedRow';

export interface NudgeFloatingStackProps {
  /**
   * MUST be a stable, module-level array — each entry is a hook. See `useNudges`.
   */
  sources: NudgeSource[];
  /** Total cap across every kind. Extra nudges are dropped, not paginated. */
  limit: number;
}

/**
 * The floating nudge surface, bottom-left above the collapsed sidebar.
 *
 * Accordion: the top-ranked nudge is expanded, the rest sit below as one-line rows the user can
 * click to bring forward. Four expanded cards would run past 600px and swallow the left side of
 * the screen, so only one body is mounted at a time — and there is still no carousel, since every
 * nudge stays on screen and reachable in one click.
 */
const NudgeFloatingStack = ({ sources, limit }: NudgeFloatingStackProps) => {
  const nudges = useNudges(sources, limit);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /*
   * Falls back to the top-ranked nudge that does NOT defer work, whenever nothing is pinned open or
   * the pinned one has left the stack (dismissed, or its source turned ineligible).
   *
   * A nudge declaring `onExpand` is skipped by that fallback on purpose: auto-expanding it would
   * run the work it deliberately deferred, and on most pages it would be the only nudge around. So
   * a stack of nothing but deferred nudges renders fully collapsed until the user picks one.
   */
  const pinned = nudges.find((nudge) => nudge.id === expandedId);
  const expandedNudge = pinned ?? nudges.find((nudge) => !nudge.onExpand);

  /*
   * Fires once per pinned nudge, keyed by ID.
   *
   * `pinned` is a fresh object every render and its `onExpand` a fresh closure, so depending on the
   * callback would re-run this on every render — harmless while `onExpand` is an idempotent
   * `setExpanded(true)`, but the contract promises "called when the user expands", and the first
   * source to put a `track()` or an imperative `refetch()` there would fire it per render. The ref
   * keeps the latest callback without making it a dependency.
   */
  const onExpandRef = useRef(pinned?.onExpand);
  onExpandRef.current = pinned?.onExpand;

  const pinnedId = pinned?.id;
  useEffect(() => {
    if (pinnedId) onExpandRef.current?.();
  }, [pinnedId]);

  if (nudges.length === 0) return null;

  return (
    <div data-testid='nudge-floating-stack' className='fixed bottom-4 left-[calc(var(--sidebar-width-icon)+1rem)] z-[1003] flex w-fit max-w-[360px] flex-col gap-2'>
      {nudges.map((nudge) =>
        nudge.id === expandedNudge?.id ? <NudgeCard key={nudge.id} nudge={nudge} /> : <NudgeCollapsedRow key={nudge.id} nudge={nudge} onExpand={() => setExpandedId(nudge.id)} />
      )}
    </div>
  );
};

export default NudgeFloatingStack;
